import React, { useState } from 'react';
import { useTransactions } from '../contexts/TransactionContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Star, Wallet, Search, Filter, Edit2, Trash2, X, Calendar as CalendarIcon, FileText, Sparkles, Download } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import AddTransactionModal from '../components/AddTransactionModal';
import ReceiptScannerModal from '../components/ReceiptScannerModal';
import ExportReportModal from '../components/ExportReportModal';
import { DeleteConfirmationDialog } from '../components/DeleteConfirmationDialog';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import Calendar from '../components/Calendar';

const Transactions = () => {
  const { transactions, selectedMonth, setSelectedMonth, loading, summary, deleteTransaction } = useTransactions();
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const handleApplyScannedReceipt = (receiptData) => {
    setEditingTransaction({
      type: 'expense',
      amount: receiptData.amount,
      description: receiptData.description,
      category: receiptData.category,
      date: new Date(receiptData.date)
    });
    setIsModalOpen(true);
  };
  const [activeTab, setActiveTab] = useState('Daily');
  const [summaryTab, setSummaryTab] = useState('Expense');
  const [descriptionSort, setDescriptionSort] = useState('Amount'); // Amount | Count | A-Z | Recent
  const [descriptionSearch, setDescriptionSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: '',
    categories: [],
    accounts: []
  });

  // Check for account filter from Accounts page
  React.useEffect(() => {
    const accountFilter = sessionStorage.getItem('accountFilter');
    if (accountFilter) {
      setFilters(prev => ({ ...prev, accounts: [accountFilter] }));
      setShowFilters(true);
      sessionStorage.removeItem('accountFilter');
    }
  }, []);

  // Clear selected date when month changes
  React.useEffect(() => {
    setSelectedDate(null);
  }, [selectedMonth]);

  const handlePreviousMonth = () => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setSelectedMonth(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setSelectedMonth(newMonth);
  };

  const handleEditTransaction = (transaction) => {
    console.log('Opening edit modal for transaction:', transaction.id);
    setEditingTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleDeleteTransaction = (transaction) => {
    console.log('Opening delete confirmation for transaction:', transaction.id);
    setDeletingTransaction(transaction);
  };

  const handleOpenAddModal = () => {
    setEditingTransaction(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTransaction(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTransaction) return;

    try {
      console.log('Deleting transaction:', deletingTransaction.id);
      await deleteTransaction(deletingTransaction.id);
      toast.success('Transaction deleted!');
      setDeletingTransaction(null);
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      toast.error('Failed to delete transaction');
    }
  };

  const handleCancelDelete = () => {
    setDeletingTransaction(null);
  };

  // Filter and search transactions
  const getFilteredAndSearchedTransactions = () => {
    let filtered = transactions;

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.description?.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query) ||
        t.amount.toString().includes(query)
      );
      console.log(`Search results for "${searchQuery}":`, filtered.length, 'transactions');
    }

    // Apply date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(t => t.date >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(t => t.date <= toDate);
    }

    // Apply amount range filter
    if (filters.amountMin) {
      filtered = filtered.filter(t => t.amount >= parseFloat(filters.amountMin));
    }
    if (filters.amountMax) {
      filtered = filtered.filter(t => t.amount <= parseFloat(filters.amountMax));
    }

    // Apply category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter(t => filters.categories.includes(t.category));
    }

    // Apply account filter (check both account and toAccount for transfers)
    if (filters.accounts.length > 0) {
      filtered = filtered.filter(t => 
        filters.accounts.includes(t.account) || 
        (t.toAccount && filters.accounts.includes(t.toAccount))
      );
    }

    console.log('Filtered transactions:', filtered.length, 'of', transactions.length);
    return filtered;
  };

  const hasActiveFilters = () => {
    return filters.dateFrom || filters.dateTo || filters.amountMin || 
           filters.amountMax || filters.categories.length > 0 || 
           filters.accounts.length > 0;
  };

  const clearAllFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: '',
      categories: [],
      accounts: []
    });
    setSearchQuery('');
    setShowSearchBar(false);
    console.log('All filters cleared');
  };

  const groupTransactionsByDate = () => {
    const groups = {};
    transactions.forEach(transaction => {
      const date = transaction.date;
      let dateKey;
      
      if (isToday(date)) {
        dateKey = 'Today';
      } else if (isYesterday(date)) {
        dateKey = 'Yesterday';
      } else {
        dateKey = format(date, 'MMM d, yyyy');
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(transaction);
    });
    
    return groups;
  };

  const groupedTransactions = groupTransactionsByDate();

  const tabs = ['Daily', 'Calendar', 'Monthly', 'Summary', 'Description'];

  const renderTabContent = () => {
    // Use filtered transactions instead of raw transactions
    const displayTransactions = getFilteredAndSearchedTransactions();
    const groupedFiltered = displayTransactions.reduce((groups, transaction) => {
      const date = transaction.date;
      let dateKey;
      
      if (isToday(date)) {
        dateKey = 'Today';
      } else if (isYesterday(date)) {
        dateKey = 'Yesterday';
      } else {
        dateKey = format(date, 'MMM d, yyyy');
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(transaction);
      
      return groups;
    }, {});

    switch(activeTab) {
      case 'Daily':
      case 'Monthly':
        // Show the regular transaction list
        return (
          <div className="mb-20">
            {loading ? (
              <div className="text-center py-12">
                <p style={{ color: theme.textSecondary }}>Loading transactions...</p>
              </div>
            ) : displayTransactions.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}>
                  <Wallet className="w-8 h-8" style={{ color: theme.textSecondary }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: theme.textSecondary }}>
                  {searchQuery || hasActiveFilters() ? 'No matching transactions' : 'No transactions yet'}
                </h3>
                <p className="text-sm" style={{ color: theme.textTertiary }}>
                  {searchQuery || hasActiveFilters() ? 'Try adjusting your search or filters' : 'Tap + to add your first transaction'}
                </p>
                {(searchQuery || hasActiveFilters()) && (
                  <button
                    onClick={clearAllFilters}
                    className="mt-4 px-4 py-2 rounded-lg font-medium"
                    style={{ backgroundColor: theme.accent, color: 'white' }}
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              Object.entries(groupedFiltered).map(([date, transactions]) => (
                <div key={date} className="mb-4">
                  {/* Date Header */}
                  <div 
                    className="text-sm font-medium uppercase px-3 py-2 mb-2 rounded" 
                    style={{ backgroundColor: isDark ? theme.inputBackground : '#E9ECEF', color: theme.textSecondary }}
                  >
                    {date}
                  </div>
                  
                  {/* Transactions */}
                  <div className="space-y-2">
                    {transactions.map(transaction => (
                      <div
                        key={transaction.id}
                        className="p-3 rounded-lg flex items-center group transition-all duration-200"
                        style={{ 
                          backgroundColor: theme.cardBackground,
                          boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                          border: `1px solid ${theme.divider}`
                        }}
                        data-testid={`transaction-item-${transaction.id}`}
                      >
                        {/* Category Icon */}
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-xl"
                          style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}
                        >
                          {transaction.categoryIcon}
                        </div>
                        
                        {/* Transaction Details */}
                        <div className="flex-1">
                          <div className="font-semibold" style={{ color: theme.text }}>
                            {transaction.category}
                          </div>
                          <div className="text-sm" style={{ color: theme.textSecondary }}>
                            {transaction.type === 'transfer' && transaction.toAccount
                              ? `${transaction.account} ➔ ${transaction.toAccount}`
                              : transaction.account} • {format(transaction.date, 'h:mm a')}
                          </div>
                          {transaction.description && (
                            <div className="text-sm mt-1" style={{ color: theme.textTertiary }}>
                              {transaction.description}
                            </div>
                          )}
                        </div>
                        
                        {/* Amount */}
                        <div 
                          className="text-lg font-bold mr-3"
                          style={{ 
                            color: transaction.type === 'income' 
                              ? theme.accentBlue 
                              : (transaction.type === 'transfer' ? '#9C27B0' : theme.accent) 
                          }}
                        >
                          {transaction.type === 'income' ? '+' : (transaction.type === 'transfer' ? '↔ ' : '- ')}{formatCurrency(transaction.amount)}
                        </div>

                        {/* Edit/Delete Buttons (visible on hover) */}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => handleEditTransaction(transaction)}
                            className="p-2 rounded-lg transition-colors"
                            style={{ backgroundColor: 'transparent' }}
                            title="Edit transaction"
                            data-testid={`edit-transaction-${transaction.id}`}
                          >
                            <Edit2 className="w-4 h-4" style={{ color: theme.textSecondary }} />
                          </button>
                          <button
                            onClick={() => handleDeleteTransaction(transaction)}
                            className="p-2 rounded-lg transition-colors"
                            title="Delete transaction"
                            data-testid={`delete-transaction-${transaction.id}`}
                          >
                            <Trash2 className="w-4 h-4" style={{ color: theme.accent }} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        );
      
      case 'Calendar': {
        const monthlyTransactions = transactions;
        const hasAnyMonthlyTransactions = monthlyTransactions.length > 0;

        const transactionsForSelectedDate = selectedDate
          ? monthlyTransactions.filter((t) => isSameDay(t.date, selectedDate))
          : [];

        const dailyTotal = transactionsForSelectedDate.reduce((sum, t) => {
          if (t.type === 'income') return sum + t.amount;
          if (t.type === 'expense') return sum - t.amount;
          return sum;
        }, 0);

        return (
          <div className="mb-20 space-y-6">
            {/* Calendar Grid */}
            <Calendar
              selectedMonth={selectedMonth}
              transactions={getFilteredAndSearchedTransactions()}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />

            {/* Monthly empty state (no transactions at all) */}
            {!hasAnyMonthlyTransactions && (
              <div className="text-center py-12">
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                  style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}
                >
                  <CalendarIcon className="w-8 h-8" style={{ color: theme.textSecondary }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: theme.textSecondary }}>
                  No transactions this month
                </h3>
                <p className="text-sm mb-2" style={{ color: theme.textTertiary }}>
                  Tap + to add one.
                </p>
              </div>
            )}

            {/* Selected date transactions */}
            {hasAnyMonthlyTransactions && (
              <div className="mt-4">
                {selectedDate ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
                        Transactions on {format(selectedDate, 'MMM d, yyyy')}
                      </h3>
                      <div className="text-sm font-medium" style={{ color: theme.textSecondary }}>
                        Total:{' '}
                        <span
                          style={{
                            color:
                              dailyTotal > 0
                                ? theme.income
                                : dailyTotal < 0
                                ? theme.accent
                                : theme.text,
                          }}
                        >
                          {formatCurrency(dailyTotal)}
                        </span>
                      </div>
                    </div>

                    {transactionsForSelectedDate.length === 0 ? (
                      <div className="text-center py-8">
                        <p style={{ color: theme.textSecondary }}>
                          No transactions on this date
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {transactionsForSelectedDate.map((transaction) => (
                          <div
                            key={transaction.id}
                            className="p-3 rounded-lg flex items-center group transition-all duration-200"
                            style={{
                              backgroundColor: theme.cardBackground,
                              boxShadow: isDark
                                ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                : '0 2px 8px rgba(0, 0, 0, 0.08)',
                              border: `1px solid ${theme.divider}`,
                            }}
                            data-testid={`transaction-item-calendar-${transaction.id}`}
                          >
                            {/* Category Icon */}
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-xl"
                              style={{
                                backgroundColor: isDark
                                  ? theme.inputBackground
                                  : '#F8F9FA',
                              }}
                            >
                              {transaction.categoryIcon}
                            </div>

                            {/* Transaction Details */}
                            <div className="flex-1">
                              <div
                                className="font-semibold"
                                style={{ color: theme.text }}
                              >
                                {transaction.category}
                              </div>
                              <div
                                className="text-sm"
                                style={{ color: theme.textSecondary }}
                              >
                                {transaction.account} • {format(transaction.date, 'h:mm a')}
                              </div>
                              {transaction.description && (
                                <div
                                  className="text-sm mt-1"
                                  style={{ color: theme.textTertiary }}
                                >
                                  {transaction.description}
                                </div>
                              )}
                            </div>

                            {/* Amount */}
                            <div
                              className="text-lg font-bold mr-3"
                              style={{
                                color:
                                  transaction.type === 'income'
                                    ? theme.accentBlue
                                    : theme.accent,
                              }}
                            >
                              {transaction.type === 'income' ? '+' : '-'}{' '}
                              {formatCurrency(transaction.amount)}
                            </div>

                            {/* Edit/Delete Buttons */}
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <button
                                onClick={() => handleEditTransaction(transaction)}
                                className="p-2 rounded-lg transition-colors"
                                style={{ backgroundColor: 'transparent' }}
                                title="Edit transaction"
                              >
                                <Edit2
                                  className="w-4 h-4"
                                  style={{ color: theme.textSecondary }}
                                />
                              </button>
                              <button
                                onClick={() => handleDeleteTransaction(transaction)}
                                className="p-2 rounded-lg transition-colors"
                                title="Delete transaction"
                              >
                                <Trash2
                                  className="w-4 h-4"
                                  style={{ color: theme.accent }}
                                />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : hasAnyMonthlyTransactions ? (
                  <div className="text-center py-6">
                    <p className="text-sm" style={{ color: theme.textSecondary }}>
                      Tap a date in the calendar to view its transactions.
                    </p>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      }
      
      case 'Summary':
        // Use filtered + searched transactions for consistency
        const monthlyTx = getFilteredAndSearchedTransactions();
        const hasAnyTx = monthlyTx.length > 0;

        const incomeTx = monthlyTx.filter(t => t.type === 'income');
        const expenseTx = monthlyTx.filter(t => t.type === 'expense');

        const totalIncome = incomeTx.reduce((sum, t) => sum + t.amount, 0);
        const totalExpense = expenseTx.reduce((sum, t) => sum + t.amount, 0);
        const netTotal = totalIncome - totalExpense;

        // Group by category
        const incomeByCategory = incomeTx.reduce((acc, t) => {
          acc[t.category] = (acc[t.category] || 0) + t.amount;
          return acc;
        }, {});

        const expenseByCategory = expenseTx.reduce((acc, t) => {
          acc[t.category] = (acc[t.category] || 0) + t.amount;
          return acc;
        }, {});

        const incomePalette = ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE'];
        const expensePalette = ['#EF4444', '#F87171', '#FCA5A5', '#FECACA', '#FEE2E2'];

        const incomeCategoryData = Object.entries(incomeByCategory)
          .map(([name, value], index) => ({
            name,
            value,
            color: incomePalette[index % incomePalette.length],
            percentage: totalIncome ? (value / totalIncome) * 100 : 0,
          }))
          .sort((a, b) => b.value - a.value);

        const expenseCategoryData = Object.entries(expenseByCategory)
          .map(([name, value], index) => ({
            name,
            value,
            color: expensePalette[index % expensePalette.length],
            percentage: totalExpense ? (value / totalExpense) * 100 : 0,
          }))
          .sort((a, b) => b.value - a.value);

        const incomeChartData = incomeCategoryData.map(d => ({
          name: d.name,
          value: d.value,
          color: d.color,
        }));

        const expenseChartData = expenseCategoryData.map(d => ({
          name: d.name,
          value: d.value,
          color: d.color,
        }));

        // Account breakdown
        const accountMap = monthlyTx.reduce((acc, t) => {
          const key = t.account || 'Unknown';
          if (!acc[key]) {
            acc[key] = { income: 0, expense: 0 };
          }
          if (t.type === 'income') acc[key].income += t.amount;
          if (t.type === 'expense') acc[key].expense += t.amount;
          return acc;
        }, {});

        const accountData = Object.entries(accountMap)
          .map(([name, vals]) => ({
            name,
            income: vals.income,
            expense: vals.expense,
            net: vals.income - vals.expense,
          }))
          .sort((a, b) => b.net - a.net);

        const summaryTabs = ['Income', 'Expense', 'Account'];

        const handleCategoryClick = (category) => {
          setFilters(prev => ({
            ...prev,
            categories: [category],
          }));
          setActiveTab('Daily');
        };

        const renderSummaryList = () => {
          if (summaryTab === 'Income') {
            if (!totalIncome) {
              return (
                <div className="text-center py-6" style={{ color: theme.textSecondary }}>
                  No income data for this period.
                </div>
              );
            }
            return (
              <div className="space-y-3">
                {incomeCategoryData.map(item => (
                  <button
                    key={item.name}
                    onClick={() => handleCategoryClick(item.name)}
                    className="w-full text-left p-3 rounded-lg transition-colors"
                    style={{
                      backgroundColor: theme.cardBackground,
                      boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
                      border: `1px solid ${theme.divider}`,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
                          style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}
                        >
                          ₹
                        </div>
                        <div>
                          <div className="font-medium" style={{ color: theme.text }}>
                            {item.name}
                          </div>
                          <div className="text-xs" style={{ color: theme.textSecondary }}>
                            {item.percentage.toFixed(1)}% of income
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold" style={{ color: '#3B82F6' }}>
                          {formatCurrency(item.value)}
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(item.percentage, 100)}%`,
                          backgroundColor: '#3B82F6',
                        }}
                      />
                    </div>
                  </button>
                ))}
              </div>
            );
          }

          if (summaryTab === 'Expense') {
            if (!totalExpense) {
              return (
                <div className="text-center py-6" style={{ color: theme.textSecondary }}>
                  No expense data for this period.
                </div>
              );
            }
            return (
              <div className="space-y-3">
                {expenseCategoryData.map(item => (
                  <button
                    key={item.name}
                    onClick={() => handleCategoryClick(item.name)}
                    className="w-full text-left p-3 rounded-lg transition-colors"
                    style={{
                      backgroundColor: theme.cardBackground,
                      boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
                      border: `1px solid ${theme.divider}`,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
                          style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}
                        >
                          ₹
                        </div>
                        <div>
                          <div className="font-medium" style={{ color: theme.text }}>
                            {item.name}
                          </div>
                          <div className="text-xs" style={{ color: theme.textSecondary }}>
                            {item.percentage.toFixed(1)}% of expenses
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold" style={{ color: '#EF4444' }}>
                          {formatCurrency(item.value)}
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(item.percentage, 100)}%`,
                          backgroundColor: '#EF4444',
                        }}
                      />
                    </div>
                  </button>
                ))}
              </div>
            );
          }

          // Account tab
          if (!accountData.length) {
            return (
              <div className="text-center py-6" style={{ color: theme.textSecondary }}>
                No account data for this period.
              </div>
            );
          }

          const maxAbsNet = Math.max(...accountData.map(a => Math.abs(a.net)), 1);

          return (
            <div className="space-y-3">
              {accountData.map(acc => {
                const total = acc.income + acc.expense || 1;
                const incomePct = (acc.income / total) * 100;
                const expensePct = (acc.expense / total) * 100;
                return (
                  <div
                    key={acc.name}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: theme.cardBackground,
                      boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
                      border: `1px solid ${theme.divider}`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium" style={{ color: theme.text }}>
                        {acc.name}
                      </div>
                      <div className="text-right text-xs" style={{ color: theme.textSecondary }}>
                        <div>
                          <span style={{ color: '#3B82F6' }}>+{formatCurrency(acc.income)}</span>
                        </div>
                        <div>
                          <span style={{ color: '#EF4444' }}>-{formatCurrency(acc.expense)}</span>
                        </div>
                        <div style={{ color: acc.net >= 0 ? theme.income : theme.accent }}>
                          Net {formatCurrency(acc.net)}
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1 overflow-hidden flex">
                      <div
                        style={{
                          width: `${incomePct}%`,
                          backgroundColor: '#3B82F6',
                        }}
                      />
                      <div
                        style={{
                          width: `${expensePct}%`,
                          backgroundColor: '#EF4444',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        };

        return (
          <div className="mb-20 space-y-6">
            {/* Charts row */}
            <div className="flex flex-col lg:flex-row gap-6 items-center lg:items-start">
              {/* Income chart */}
              <div
                className="rounded-lg p-4 w-full max-w-xs sm:max-w-sm lg:max-w-md"
                style={{
                  backgroundColor: theme.cardBackground,
                  boxShadow: theme.shadow,
                  border: `1px solid ${theme.divider}`,
                }}
              >
                <h3 className="text-sm font-semibold mb-2" style={{ color: theme.text }}>
                  Income
                </h3>
                <div className="flex justify-center items-center">
                  <div className="relative w-44 h-44 sm:w-52 sm:h-52 lg:w-64 lg:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={incomeChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          labelLine={false}
                        >
                          {incomeChartData.map((entry, index) => (
                            <Cell key={`inc-cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) =>
                            [`${formatCurrency(value)} (${((value / (totalIncome || 1)) * 100).toFixed(1)}%)`, name]
                          }
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <div className="text-xs" style={{ color: theme.textSecondary }}>
                        Total
                      </div>
                      <div className="text-sm font-semibold" style={{ color: '#3B82F6' }}>
                        {totalIncome ? formatCurrency(totalIncome) : 'No data'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expense chart */}
              <div
                className="rounded-lg p-4 w-full max-w-xs sm:max-w-sm lg:max-w-md"
                style={{
                  backgroundColor: theme.cardBackground,
                  boxShadow: theme.shadow,
                  border: `1px solid ${theme.divider}`,
                }}
              >
                <h3 className="text-sm font-semibold mb-2" style={{ color: theme.text }}>
                  Expense
                </h3>
                <div className="flex justify-center items-center">
                  <div className="relative w-44 h-44 sm:w-52 sm:h-52 lg:w-64 lg:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={expenseChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          labelLine={false}
                        >
                          {expenseChartData.map((entry, index) => (
                            <Cell key={`exp-cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) =>
                            [`${formatCurrency(value)} (${((value / (totalExpense || 1)) * 100).toFixed(1)}%)`, name]
                          }
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <div className="text-xs" style={{ color: theme.textSecondary }}>
                        Total
                      </div>
                      <div className="text-sm font-semibold" style={{ color: '#EF4444' }}>
                        {totalExpense ? formatCurrency(totalExpense) : 'No data'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Empty state note when no transactions at all */}
            {!hasAnyTx && (
              <div className="text-center py-4">
                <p className="text-sm" style={{ color: theme.textSecondary }}>
                  Add transactions to see income and expense breakdown.
                </p>
              </div>
            )}

            {/* Net balance summary card */}
            <div
              className="rounded-lg p-4"
              style={{
                backgroundColor: theme.cardBackground,
                boxShadow: theme.shadow,
                border: `1px solid ${theme.divider}`,
              }}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium" style={{ color: theme.textSecondary }}>
                  Net Balance
                </div>
                <div
                  className="text-xl font-bold"
                  style={{
                    color:
                      netTotal > 0 ? theme.income : netTotal < 0 ? theme.accent : theme.text,
                  }}
                >
                  {formatCurrency(netTotal)}
                </div>
              </div>
            </div>

            {/* Category filter badge (if any) */}
            {filters.categories.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: theme.textSecondary }}>
                  Filtered by:
                </span>
                {filters.categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() =>
                      setFilters(prev => ({
                        ...prev,
                        categories: prev.categories.filter(c => c !== cat),
                      }))
                    }
                    className="px-2 py-1 rounded-full text-xs flex items-center gap-1"
                    style={{
                      backgroundColor: isDark ? theme.inputBackground : '#FEE2E2',
                      color: theme.accent,
                    }}
                  >
                    {cat}
                    <X className="w-3 h-3" />
                  </button>
                ))}
              </div>
            )}

            {/* Summary tabs */}
            <div className="mt-4">
              <div className="flex gap-4 border-b" style={{ borderColor: theme.divider }}>
                {summaryTabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setSummaryTab(tab)}
                    className="pb-2 text-sm font-medium relative"
                    style={{
                      color: summaryTab === tab ? theme.text : theme.textSecondary,
                    }}
                  >
                    {tab}
                    {summaryTab === tab && (
                      <div
                        className="absolute left-0 right-0 -bottom-px h-1"
                        style={{ backgroundColor: theme.accent, borderRadius: '2px 2px 0 0' }}
                      />
                    )}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                {renderSummaryList()}
              </div>
            </div>
          </div>
        );
      
      case 'Description':
        // Group by description/memo
        const monthTx = displayTransactions;

        if (monthTx.length === 0) {
          return (
            <div className="text-center py-16 mb-20">
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}
              >
                <FileText className="w-8 h-8" style={{ color: theme.textSecondary }} />
              </div>
              <h3 className="text-lg font-semibold mb-1" style={{ color: theme.text }}>
                No transactions this month
              </h3>
              <p className="text-sm" style={{ color: theme.textSecondary }}>
                Add transactions to see description-based breakdown.
              </p>
            </div>
          );
        }

        const hasAnyDescription = monthTx.some(
          (t) => t.description && t.description.trim().length > 0
        );

        const groupByDescription = (txns) => {
          const groups = {};

          txns.forEach((txn) => {
            const baseDesc = txn.description && txn.description.trim().length > 0
              ? txn.description
              : txn.category;
            const key = baseDesc.toLowerCase().trim();

            if (!groups[key]) {
              groups[key] = {
                key,
                description: baseDesc,
                transactions: [],
                totalAmount: 0,
                count: 0,
                hasIncome: false,
                hasExpense: false,
                latestDate: null,
              };
            }

            const g = groups[key];
            g.transactions.push(txn);
            g.count += 1;
            g.totalAmount += txn.type === 'income' ? txn.amount : -txn.amount;
            if (txn.type === 'income') g.hasIncome = true;
            if (txn.type === 'expense') g.hasExpense = true;

            if (!g.latestDate || txn.date > g.latestDate) {
              g.latestDate = txn.date;
            }
          });

          // Sort transactions in each group by date (newest first)
          Object.values(groups).forEach((g) => {
            g.transactions.sort((a, b) => b.date - a.date);
          });

          return Object.values(groups);
        };

        let descriptionGroups = groupByDescription(monthTx);

        const totalGroups = descriptionGroups.length;
        const mostCommonGroup =
          totalGroups > 0
            ? [...descriptionGroups].sort((a, b) => b.count - a.count)[0]
            : null;
        const largestGroup =
          totalGroups > 0
            ? [...descriptionGroups].sort(
                (a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount)
              )[0]
            : null;

        // Apply local search over descriptions
        if (descriptionSearch.trim()) {
          const q = descriptionSearch.toLowerCase();
          descriptionGroups = descriptionGroups.filter((g) =>
            g.description.toLowerCase().includes(q)
          );
        }

        // Sort groups
        if (descriptionSort === 'Amount') {
          descriptionGroups.sort(
            (a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount)
          );
        } else if (descriptionSort === 'Count') {
          descriptionGroups.sort((a, b) => b.count - a.count);
        } else if (descriptionSort === 'A-Z') {
          descriptionGroups.sort((a, b) =>
            a.description.localeCompare(b.description)
          );
        } else if (descriptionSort === 'Recent') {
          descriptionGroups.sort(
            (a, b) => (b.latestDate || 0) - (a.latestDate || 0)
          );
        }

        const toggleGroup = (key) => {
          const newSet = new Set(expandedGroups);
          if (newSet.has(key)) {
            newSet.delete(key);
          } else {
            newSet.add(key);
          }
          setExpandedGroups(newSet);
        };

        const sortPills = [
          { id: 'Amount', label: 'Amount' },
          { id: 'Count', label: 'Count' },
          { id: 'A-Z', label: 'A-Z' },
          { id: 'Recent', label: 'Recent' },
        ];

        return (
          <div className="mb-20 space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: theme.cardBackground,
                  boxShadow: theme.shadow,
                  border: `1px solid ${theme.divider}`,
                }}
              >
                <div className="text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>
                  Unique Descriptions
                </div>
                <div className="text-2xl font-bold" style={{ color: theme.text }}>
                  {totalGroups}
                </div>
              </div>

              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: theme.cardBackground,
                  boxShadow: theme.shadow,
                  border: `1px solid ${theme.divider}`,
                }}
              >
                <div className="text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>
                  Most Common
                </div>
                <div className="text-sm font-semibold" style={{ color: theme.text }}>
                  {mostCommonGroup ? mostCommonGroup.description : 'N/A'}
                </div>
                {mostCommonGroup && (
                  <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                    {mostCommonGroup.count} transactions
                  </div>
                )}
              </div>

              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: theme.cardBackground,
                  boxShadow: theme.shadow,
                  border: `1px solid ${theme.divider}`,
                }}
              >
                <div className="text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>
                  Largest Group
                </div>
                <div className="text-sm font-semibold" style={{ color: theme.text }}>
                  {largestGroup ? largestGroup.description : 'N/A'}
                </div>
                {largestGroup && (
                  <div
                    className="text-xs mt-1"
                    style={{
                      color:
                        largestGroup.totalAmount > 0
                          ? theme.accentBlue
                          : largestGroup.totalAmount < 0
                          ? theme.accent
                          : theme.textSecondary,
                    }}
                  >
                    {formatCurrency(largestGroup.totalAmount)}
                  </div>
                )}
              </div>
            </div>

            {/* Insight hint when descriptions exist */}
            {!hasAnyDescription && (
              <div
                className="p-3 rounded-lg text-sm"
                style={{
                  backgroundColor: isDark ? theme.inputBackground : '#FEF3C7',
                  color: isDark ? theme.text : '#92400E',
                  border: `1px solid ${isDark ? '#D97706' : '#FCD34D'}`,
                }}
              >
                Add descriptions to your transactions (like "Lunch", "Netflix", "Gym") to
                see them grouped here.
              </div>
            )}

            {/* Search + sort row */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              {/* Local description search */}
              <div className="w-full md:max-w-sm">
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: theme.textSecondary }}
                  />
                  <input
                    type="text"
                    value={descriptionSearch}
                    onChange={(e) => setDescriptionSearch(e.target.value)}
                    placeholder="Search descriptions..."
                    className="w-full pl-9 pr-8 py-2 rounded-lg border text-sm"
                    style={{
                      borderColor: theme.divider,
                      backgroundColor: isDark ? theme.inputBackground : 'white',
                      color: theme.text,
                    }}
                  />
                  {descriptionSearch && (
                    <button
                      onClick={() => setDescriptionSearch('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" style={{ color: theme.textSecondary }} />
                    </button>
                  )}
                </div>
              </div>

              {/* Sort pills */}
              <div className="flex items-center gap-2">
                <span className="text-xs md:text-sm" style={{ color: theme.textSecondary }}>
                  Sort by:
                </span>
                <div className="flex flex-wrap gap-1">
                  {sortPills.map((pill) => (
                    <button
                      key={pill.id}
                      onClick={() => setDescriptionSort(pill.id)}
                      className="px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor:
                          descriptionSort === pill.id
                            ? theme.accent
                            : isDark
                            ? theme.inputBackground
                            : '#E5E7EB',
                        color:
                          descriptionSort === pill.id ? 'white' : theme.textSecondary,
                      }}
                    >
                      {pill.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Groups list */}
            <div className="space-y-3">
              {descriptionGroups.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm" style={{ color: theme.textSecondary }}>
                    No description groups match your search.
                  </p>
                </div>
              ) : (
                descriptionGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.key);
                  let borderColor = '#6B7280';
                  let totalColor = '#6B7280';

                  if (group.hasIncome && !group.hasExpense) {
                    borderColor = '#3B82F6';
                    totalColor = '#3B82F6';
                  } else if (group.hasExpense && !group.hasIncome) {
                    borderColor = '#EF4444';
                    totalColor = '#EF4444';
                  }

                  const ArrowIcon = isExpanded ? ChevronUp : ChevronDown;

                  return (
                    <div
                      key={group.key}
                      className="rounded-lg p-4 cursor-pointer transition-all duration-200"
                      style={{
                        backgroundColor: theme.cardBackground,
                        boxShadow: isDark
                          ? '0 1px 4px rgba(0,0,0,0.4)'
                          : '0 1px 4px rgba(0,0,0,0.1)',
                        borderLeft: `4px solid ${borderColor}`,
                        borderTop: `1px solid ${theme.divider}`,
                        borderRight: `1px solid ${theme.divider}`,
                        borderBottom: `1px solid ${theme.divider}`,
                      }}
                      onClick={() => toggleGroup(group.key)}
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <div
                            className="font-semibold text-sm sm:text-base"
                            style={{ color: theme.text }}
                          >
                            {group.description}
                          </div>
                          <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                            {group.count} transaction{group.count > 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div
                            className="text-sm sm:text-base font-semibold text-right"
                            style={{ color: totalColor }}
                          >
                            {formatCurrency(group.totalAmount)}
                          </div>
                          <ArrowIcon
                            className="w-5 h-5 flex-shrink-0"
                            style={{ color: theme.textSecondary }}
                          />
                        </div>
                      </div>

                      {/* Expanded transactions */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: theme.divider }}>
                          <div className="space-y-2">
                            {group.transactions.map((txn) => (
                              <div
                                key={txn.id}
                                className="p-2 sm:p-3 rounded-lg flex items-center group transition-colors"
                                style={{
                                  backgroundColor: isDark
                                    ? theme.inputBackground
                                    : '#F9FAFB',
                                }}
                                onClick={(e) => {
                                  // Prevent card toggle; only edit
                                  e.stopPropagation();
                                  handleEditTransaction(txn);
                                }}
                              >
                                {/* Category icon */}
                                <div
                                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mr-3 text-base sm:text-lg"
                                  style={{
                                    backgroundColor: isDark
                                      ? theme.cardBackground
                                      : '#FFFFFF',
                                    boxShadow: isDark
                                      ? '0 1px 3px rgba(0,0,0,0.4)'
                                      : '0 1px 3px rgba(0,0,0,0.1)',
                                  }}
                                >
                                  {txn.categoryIcon}
                                </div>

                                {/* Details */}
                                <div className="flex-1">
                                  <div
                                    className="text-xs sm:text-sm"
                                    style={{ color: theme.textSecondary }}
                                  >
                                    {format(txn.date, 'MMM d, yyyy')}
                                  </div>
                                  <div
                                    className="text-sm font-medium"
                                    style={{ color: theme.text }}
                                  >
                                    {txn.category}
                                  </div>
                                  <div
                                    className="text-xs"
                                    style={{ color: theme.textSecondary }}
                                  >
                                    {txn.account}
                                  </div>
                                  {txn.description &&
                                    txn.description.trim() !== group.description.trim() && (
                                      <div
                                        className="text-xs mt-1"
                                        style={{ color: theme.textTertiary }}
                                      >
                                        {txn.description}
                                      </div>
                                    )}
                                </div>

                                {/* Amount */}
                                <div
                                  className="text-sm sm:text-base font-semibold ml-3"
                                  style={{
                                    color:
                                      txn.type === 'income'
                                        ? theme.accentBlue
                                        : theme.accent,
                                  }}
                                >
                                  {txn.type === 'income' ? '+' : '-'}{' '}
                                  {formatCurrency(txn.amount)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Page Header with Search, Filter, Export and Bookmark */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Transactions</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90 shadow-sm"
              style={{
                backgroundColor: `${theme.accent}15`,
                color: theme.accent,
                border: `1px solid ${theme.accent}40`,
              }}
              title="Export to CSV & PDF Reports"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              onClick={() => setShowSearchBar(!showSearchBar)}
              className="p-2 rounded-lg transition-colors duration-200"
              title="Search transactions"
              data-testid="search-icon"
              style={{ backgroundColor: showSearchBar ? (isDark ? '#3D1F1F' : '#FEE2E2') : 'transparent' }}
            >
              <Search className="w-6 h-6" style={{ color: showSearchBar ? theme.accent : theme.text }} />
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 rounded-lg transition-colors duration-200"
              title="Filter transactions"
              data-testid="filter-icon"
              style={{ backgroundColor: showFilters ? (isDark ? '#3D1F1F' : '#FEE2E2') : 'transparent' }}
            >
              <Filter className="w-6 h-6" style={{ color: showFilters || hasActiveFilters() ? theme.accent : theme.text }} />
            </button>
            <button
              onClick={() => toast.info('Bookmarks (Coming Soon in next update!)')}
              className="p-2 rounded-lg transition-colors duration-200"
              title="Bookmarks (Coming Soon)"
              data-testid="bookmark-icon"
            >
              <Star className="w-6 h-6" style={{ color: theme.text }} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {showSearchBar && (
          <div className="mb-4 animate-in fade-in duration-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: theme.textSecondary }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by description, category, or amount..."
                className="w-full pl-10 pr-10 py-3 rounded-lg border text-base"
                style={{ 
                  borderColor: theme.divider, 
                  backgroundColor: isDark ? theme.inputBackground : 'white',
                  color: theme.text
                }}
                autoFocus
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded"
                  title="Clear search"
                >
                  <X className="w-5 h-5" style={{ color: theme.textSecondary }} />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm mt-2" style={{ color: theme.textSecondary }}>
                Showing {getFilteredAndSearchedTransactions().length} results for &ldquo;{searchQuery}&rdquo;
              </p>
            )}
          </div>
        )}

        {/* Advanced Filter Panel */}
        {showFilters && (
          <div className="mb-4 rounded-lg p-4 animate-in fade-in duration-200" style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: theme.text }}>Filters</h3>
              {hasActiveFilters() && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm font-medium"
                  style={{ color: theme.accent }}
                >
                  Clear All
                </button>
              )}
            </div>

            <div className="space-y-4">
              {/* Date Range */}
              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: theme.text }}>Date Range</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: theme.textSecondary }}>From</Label>
                    <Input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                      className="w-full"
                      style={{ backgroundColor: isDark ? theme.inputBackground : 'white', color: theme.text, borderColor: theme.divider }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: theme.textSecondary }}>To</Label>
                    <Input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                      className="w-full"
                      style={{ backgroundColor: isDark ? theme.inputBackground : 'white', color: theme.text, borderColor: theme.divider }}
                    />
                  </div>
                </div>
              </div>

              {/* Amount Range */}
              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: theme.text }}>Amount Range ({currencyInfo.symbol})</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: theme.textSecondary }}>Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.amountMin}
                      onChange={(e) => setFilters({ ...filters, amountMin: e.target.value })}
                      className="w-full"
                      style={{ backgroundColor: isDark ? theme.inputBackground : 'white', color: theme.text, borderColor: theme.divider }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: theme.textSecondary }}>Max</Label>
                    <Input
                      type="number"
                      placeholder="999999"
                      value={filters.amountMax}
                      onChange={(e) => setFilters({ ...filters, amountMax: e.target.value })}
                      className="w-full"
                      style={{ backgroundColor: isDark ? theme.inputBackground : 'white', color: theme.text, borderColor: theme.divider }}
                    />
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: theme.text }}>Categories</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {[...new Set(transactions.map(t => t.category))].sort().map((category) => (
                    <div key={category} className="flex items-center space-x-2">
                      <Checkbox
                        id={`cat-${category}`}
                        checked={filters.categories.includes(category)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFilters({ ...filters, categories: [...filters.categories, category] });
                          } else {
                            setFilters({ ...filters, categories: filters.categories.filter(c => c !== category) });
                          }
                        }}
                      />
                      <label
                        htmlFor={`cat-${category}`}
                        className="text-sm cursor-pointer"
                        style={{ color: theme.text }}
                      >
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Accounts */}
              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: theme.text }}>Accounts</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[...new Set(transactions.map(t => t.account))].sort().map((account) => (
                    <div key={account} className="flex items-center space-x-2">
                      <Checkbox
                        id={`acc-${account}`}
                        checked={filters.accounts.includes(account)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFilters({ ...filters, accounts: [...filters.accounts, account] });
                          } else {
                            setFilters({ ...filters, accounts: filters.accounts.filter(a => a !== account) });
                          }
                        }}
                      />
                      <label
                        htmlFor={`acc-${account}`}
                        className="text-sm cursor-pointer"
                        style={{ color: theme.text }}
                      >
                        {account}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Apply/Clear Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setShowFilters(false)}
                  className="flex-1"
                  style={{ backgroundColor: theme.accent, color: 'white' }}
                >
                  Apply Filters ({getFilteredAndSearchedTransactions().length})
                </Button>
                <Button
                  onClick={clearAllFilters}
                  variant="outline"
                  className="flex-1"
                  style={{ borderColor: theme.divider, color: theme.text }}
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Month Navigator */}
        <div className="flex items-center justify-center mb-6">
          <button
            onClick={handlePreviousMonth}
            className="p-2 rounded-lg transition-colors duration-200"
            style={{ backgroundColor: 'transparent' }}
          >
            <ChevronLeft className="w-6 h-6" style={{ color: theme.textSecondary }} />
          </button>
          
          <h2 className="text-2xl font-bold mx-6" style={{ color: theme.text }}>
            {format(selectedMonth, 'MMMM yyyy')}
          </h2>
          
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg transition-colors duration-200"
            style={{ backgroundColor: 'transparent' }}
          >
            <ChevronRight className="w-6 h-6" style={{ color: theme.textSecondary }} />
          </button>
        </div>

        {/* Tab Bar (Daily/Calendar/Monthly/Summary/Description) */}
        <div className="mb-6 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div className="flex gap-2 min-w-max pb-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="px-4 py-3 font-medium text-base transition-all duration-200 relative whitespace-nowrap"
                style={{
                  color: activeTab === tab ? theme.text : theme.textSecondary,
                  fontWeight: activeTab === tab ? 600 : 400,
                }}
                data-testid={`tab-${tab.toLowerCase()}`}
              >
                {tab}
                {activeTab === tab && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{
                      backgroundColor: theme.accent,
                      borderRadius: '2px 2px 0 0'
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {/* Income Card */}
          <div 
            className="p-4 rounded-lg" 
            style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
          >
            <div className="text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>Income</div>
            <div className="text-2xl font-bold" style={{ color: theme.accentBlue }}>
              {formatCurrency(summary.income)}
            </div>
          </div>

          {/* Expense Card */}
          <div 
            className="p-4 rounded-lg" 
            style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
          >
            <div className="text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>Expense</div>
            <div className="text-2xl font-bold" style={{ color: theme.accent }}>
              {formatCurrency(summary.expense)}
            </div>
          </div>

          {/* Total Card */}
          <div 
            className="p-4 rounded-lg" 
            style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
          >
            <div className="text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>Total</div>
            <div 
              className="text-2xl font-bold" 
              style={{ 
                color: summary.total > 0 ? theme.income : summary.total < 0 ? theme.accent : theme.text 
              }}
            >
              {formatCurrency(summary.total)}
            </div>
          </div>
        </div>

        {/* Tab Content (Transaction List or Placeholder Views) */}
        {renderTabContent()}

        {/* Floating Action Buttons */}
        <div className="fixed bottom-24 md:bottom-6 right-6 flex flex-col items-end gap-3 z-40">
          <button
            onClick={() => setIsScannerOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full font-bold text-xs md:text-sm shadow-xl transition-all duration-200 hover:scale-105 active:scale-95"
            style={{ 
              backgroundColor: isDark ? '#252C38' : '#FFFFFF',
              color: theme.accent,
              border: `1.5px solid ${theme.accent}`,
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(232, 84, 79, 0.18)'
            }}
            title="Scan paper receipt or digital invoice with OCR"
          >
            <Sparkles className="w-4 h-4" />
            Scan Receipt (OCR)
          </button>

          <button
            onClick={handleOpenAddModal}
            className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-xl"
            style={{ 
              backgroundColor: theme.accent,
              boxShadow: '0 4px 14px rgba(232, 84, 79, 0.4)',
            }}
            data-testid="add-transaction-fab"
            title="Add transaction manually"
          >
            <Plus className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* OCR Receipt Scanner Modal */}
        <ReceiptScannerModal
          isOpen={isScannerOpen}
          onClose={() => setIsScannerOpen(false)}
          onApplyReceipt={handleApplyScannedReceipt}
        />

        {/* Export Reports Modal (CSV & PDF) */}
        <ExportReportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
        />

        {/* Add/Edit Transaction Modal */}
        <AddTransactionModal 
          isOpen={isModalOpen} 
          onClose={handleCloseModal}
          editTransaction={editingTransaction}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={!!deletingTransaction}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          transactionDescription={
            deletingTransaction
              ? deletingTransaction.description
                ? `${deletingTransaction.description} — ${formatCurrency(deletingTransaction.amount)}`
                : `${deletingTransaction.category} — ${formatCurrency(deletingTransaction.amount)}`
              : ''
          }
        />
      </div>
    </Layout>
  );
};

export default Transactions;
